import { apiClient } from '../infra/http/apiClient'

export const formatUser = (user) => {
  const familyMembers = user.familyMembers || []
  return {
    ...user,
    value: user.resourceId,
    name: `${user.firstName} ${user.lastName}`,
    roles: user.roles || [],
    sports: user.sports || [],
    phoneNumber: user.phoneNumber || '',
    certificates: user.instructorCertificates || [],
    weChatId: user.weChatId || '',
    familyMembers: familyMembers.map((m) => ({
      ...m,
      name: `${m.firstName} ${m.lastName}`,
    })),
  }
}

// 反向格式化用户数据（移除格式化添加的字段）
const formatReverseUser = (user) => {
  const cleanUser = { ...user }
  const familyMembers = user.familyMembers || []
  const cleanedFamilyMembers = familyMembers.map((m) => {
    const member = { ...m }
    delete member.name
    return member
  })

  delete cleanUser.value
  delete cleanUser.name
  delete cleanUser.certificates
  cleanUser.familyMembers = cleanedFamilyMembers
  return cleanUser
}

class UserService {
  /**
   * Fetch all users
   * @param {Object} filters - Optional filters
   * @param {string} [filters.role] - Filter by role (USER, INSTRUCTOR, ADMIN)
   * @param {string} [filters.search] - Search keyword
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object containing user list
   */
  static async fetchUsers(filters = {}) {
    try {
      const userIds = filters.userIds
      if (userIds) {
        filters.userIds = userIds.join(',')
      }
      const response = await apiClient.get('/users', {
        params: { ...filters },
        paramsSerializer: {
          encode: (param) => encodeURIComponent(param),
        },
      })
      return response.data.map(formatUser)
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Fetch single user information
   * @param {string} userId - User ID
   * @returns {Promise} Promise object containing user information
   */
  static async fetchUser(userId) {
    if (!userId) return null
    try {
      const response = await apiClient.get(`/user/${userId}`)
      return formatUser(response.data)
    } catch (error) {
      console.error('Failed to fetch user:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Update user information
   * @param {string} userId - User ID
   * @param {Object} userData - User data to update
   * @param {string} userData.firstName - First name
   * @param {string} userData.lastName - Last name
   * @param {string} userData.email - Email
   * @param {string[]} userData.roles - Role list
   * @param {string[]} userData.sports - Sports list
   * @param {string} userData.clubMembershipId - Club membership ID
   * @returns {Promise} Promise object containing updated user information
   */
  static async updateUser(userId, userData) {
    try {
      const response = await apiClient.put(`/user/${userId}`, formatReverseUser(userData))
      return response.data
    } catch (error) {
      console.error('Failed to update user:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Delete user
   * @param {string} userId - User ID to delete
   * @returns {Promise} Promise object containing deletion result
   */
  static async deleteUser(userId) {
    try {
      const response = await apiClient.delete(`/user/${userId}`)
      return response.data
    } catch (error) {
      console.error('Failed to delete user:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Fetch all instructors
   * @param {Object} filters - Optional filters
   * @param {string[]} [filters.sports] - Filter by sports
   * @param {boolean} [filters.certificateVerified] - Filter by certificate verification status
   * @param {number} [filters.page=0] - Page number
   * @param {number} [filters.size=20] - Page size
   * @returns {Promise} Promise object containing instructor list
   */
  static async fetchInstructors(filters = {}) {
    try {
      const response = await apiClient.get('/users', {
        params: { ...filters, role: 'INSTRUCTOR' },
      })
      return response.data.map(formatUser)
    } catch (error) {
      console.error('Failed to fetch instructors:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Handle API errors and convert to user-friendly messages
   * @private
   */
  static handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request, please check your input.')
        case 404:
          return new Error('User not found.')
        case 401:
          return new Error('Please login to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 503:
          if (data?.code === 'MAINTENANCE_MODE') {
            return new Error(data.message || 'The system is currently under maintenance. Please try again later.')
          }
          return new Error(data.message || 'Service temporarily unavailable. Please try again later.')
        default:
          return new Error('An unexpected error occurred. Please try again later.')
      }
    }
    return new Error('Unable to connect to server. Please check your network connection.')
  }
}

export default UserService
